import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { SocialProvider } from "@/context/SocialContext";
import { CallProvider } from "@/context/CallContext";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import AuthCallback from "@/pages/AuthCallback";
import ChatApp from "@/pages/ChatApp";
import ProfilePage from "@/pages/ProfilePage";
import SettingsPage from "@/pages/SettingsPage";
import FriendsPage from "@/pages/FriendsPage";
import MapPage from "@/pages/MapPage";
import ReelsPage from "@/pages/ReelsPage";
import CallHistoryPage from "@/pages/CallHistoryPage";
import AdminPage from "@/pages/AdminPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import FriendNotifier from "@/components/social/FriendNotifier";
import CallOverlay from "@/components/calls/CallOverlay";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]" data-testid="app-loading">
      <div className="bg-white border-hard shadow-hard rounded-2xl px-6 py-4 font-display text-xl">
        Yükleniyor…
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/app/chats" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <>
      <FriendNotifier />
      <Routes>
      <Route path="/" element={<Navigate to="/app/chats" replace />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/verify-email" element={<VerifyEmailPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/app" element={<Navigate to="/app/chats" replace />} />
      <Route path="/app/chats" element={<ProtectedRoute><ChatApp /></ProtectedRoute>} />
      <Route path="/app/friends" element={<ProtectedRoute><FriendsPage /></ProtectedRoute>} />
      <Route path="/app/map" element={<ProtectedRoute><MapPage /></ProtectedRoute>} />
      <Route path="/app/reels" element={<ProtectedRoute><ReelsPage /></ProtectedRoute>} />
      <Route path="/app/calls" element={<ProtectedRoute><CallHistoryPage /></ProtectedRoute>} />
      <Route path="/app/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/app/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/app/user/:id" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/app/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/app/chats" replace />} />
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SocialProvider>
            <CallProvider>
              <AppRouter />
              <CallOverlay />
            </CallProvider>
          </SocialProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
